import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Input from '../../components/ui/Input';
import DrawingCard from './components/DrawingCard';
import UploadDrawingModal from './components/UploadDrawingModal';
import DrawingVersionsModal from './components/DrawingVersionsModal';
import { drawingService } from '../../services/drawingService';
import { siteService } from '../../services/siteService';
import { companyService } from '../../services/companyService';
import { useAuth } from '../../contexts/AuthContext';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import useSiteReadOnly, { isSiteReadOnlyStatus } from '../../hooks/useSiteReadOnly';
import SiteReadOnlyBanner from '../../components/SiteReadOnlyBanner';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';

import EmptyState from '../../components/ui/EmptyState';
import Skeleton from '../../components/ui/Skeleton';

const DrawingsPlans = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const filterSiteId = searchParams?.get('siteId');

  const [drawings, setDrawings] = useState([]);
  const [sites, setSites] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSite, setSelectedSite] = useState('all');
  const gatedSiteId = selectedSite && selectedSite !== 'all' ? selectedSite : null;
  const { status: gatedSiteStatus, isReadOnly: gatedSiteReadOnly } = useSiteReadOnly(gatedSiteId);
  const [selectedDrawingType, setSelectedDrawingType] = useState('all');
  const [showLatestOnly, setShowLatestOnly] = useState(true);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isVersionsModalOpen, setIsVersionsModalOpen] = useState(false);
  const [selectedDrawing, setSelectedDrawing] = useState(null);

  const breadcrumbItems = [
    { label: t('home'), href: '/admin-dashboard' },
    { label: t('drawingsPlans') }
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (filterSiteId && sites?.length > 0) {
      setSelectedSite(filterSiteId);
    }
  }, [filterSiteId, sites]);

  const loadData = async () => {
    try {
      setLoading(true);

      const companyResult = await companyService?.getUserCompany();

      if (companyResult?.error) {
        console.warn('[DrawingsPlans] Company lookup warning:', companyResult?.error?.message);
      } else {
        setCompanyId(companyResult?.data?.id);
      }

      // Scope sites to company (RLS ensures site_manager only sees their company's sites)
      const companyId = companyResult?.data?.id;
      const sitesResult = companyId
        ? await siteService?.getSitesByCompany(companyId)
        : await siteService?.getAllSites();

      if (sitesResult?.error) throw sitesResult.error;

      const sitesList = sitesResult?.data || [];
      setSites(sitesList);

      // Load drawings for all resolved sites
      await loadDrawings(sitesList);
    } catch (err) {
      console.error('[DrawingsPlans] load drawings failed:', err);
      setError('Failed to load drawings. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const loadDrawings = async (sitesList) => {
    try {
      const allDrawings = [];
      
      for (const site of sitesList) {
        const result = await drawingService?.getDrawingsBySite(site?.id);
        if (result?.data) {
          allDrawings?.push(...result?.data?.map(d => ({ ...d, site_name: site?.site_name })));
        }
      }
      
      setDrawings(allDrawings);
    } catch (err) {
      console.error('Error loading drawings:', err);
    }
  };

  const filteredDrawings = drawings?.filter(drawing => {
    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery?.toLowerCase();
      const matchesSearch = 
        drawing?.drawing_name?.toLowerCase()?.includes(searchLower) ||
        drawing?.drawing_number?.toLowerCase()?.includes(searchLower) ||
        drawing?.site_name?.toLowerCase()?.includes(searchLower) ||
        drawing?.description?.toLowerCase()?.includes(searchLower);
      if (!matchesSearch) return false;
    }

    if (selectedSite !== 'all' && drawing?.site_id !== selectedSite) return false;

    // Drawing type filter. Predicate is case-insensitive on the right side
    // because production data has both lowercase (current modal) and Title-case
    // (legacy path) discipline values for the same concept. The 'uncategorised'
    // branch covers NULL and empty-string discipline rows — the bulk of real
    // data, because the site-detail Drawings tab upload never captures
    // discipline. See o6Z7brIJ.
    if (selectedDrawingType !== 'all') {
      if (selectedDrawingType === 'uncategorised') {
        if (drawing?.discipline != null && drawing?.discipline !== '') return false;
      } else if (drawing?.discipline?.toLowerCase() !== selectedDrawingType) return false;
    }

    if (showLatestOnly && !drawing?.is_latest) return false;
    return true;
  });

  const handleUploadDrawing = async (drawingData, file) => {
    // Submission-time site-status check (the modal picks its own siteId
    // independently of the page-level filter).
    if (drawingData?.site_id) {
      const { data: pickedSite } = await supabase
        ?.from('sites')
        ?.select('status')
        ?.eq('id', drawingData?.site_id)
        ?.maybeSingle();
      if (isSiteReadOnlyStatus(pickedSite?.status)) {
        toast?.error('This site is completed — please reactivate to continue.');
        return;
      }
    }

    const result = await drawingService?.uploadDrawing(file, {
      ...drawingData,
      companyId
    });

    if (result?.error) {
      setError(result?.error?.message);
    } else {
      setIsUploadModalOpen(false);
      loadData();
    }
  };

  const handleViewVersions = (drawing) => {
    setSelectedDrawing(drawing);
    setIsVersionsModalOpen(true);
  };

  const handleDownload = async (drawing) => {
    const result = await drawingService?.getDrawingFileUrl(drawing?.file_path);
    if (result?.url) {
      window?.open(result?.url, '_blank');
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedSite('all');
    setSelectedDrawingType('all');
  };

  const hasActiveFilters = searchQuery || selectedSite !== 'all' || selectedDrawingType !== 'all';

  const siteOptions = [
    { value: 'all', label: t('allSites') },
    ...sites?.map(site => ({
      value: site?.id,
      label: site?.site_name
    }))
  ];

  // Filter values are lowercase to match the case-insensitive predicate;
  // the underlying DB column is free-text. Set aligned with the upload modal
  // option list (UploadDrawingModal.jsx) so users can't pick a filter value
  // that no upload can ever produce. 'site_plan' was dropped because zero
  // rows use it across all tenants. See o6Z7brIJ.
  const drawingTypeOptions = [
    { value: 'all', label: t('allTypes') },
    { value: 'uncategorised', label: 'Uncategorised' },
    { value: 'architectural', label: t('architectural') },
    { value: 'structural', label: t('structural') },
    { value: 'mechanical', label: t('mechanical') },
    { value: 'electrical', label: t('electrical') },
    { value: 'plumbing', label: t('plumbing') },
    { value: 'civil', label: t('civil') },
    { value: 'fire_protection', label: t('fireProtection') },
    { value: 'landscape', label: t('landscape') },
    { value: 'other', label: t('other') }
  ];

  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const canUpload = (userProfile?.role === 'admin' || userProfile?.role === 'site_manager') && !subscriptionReadOnly && !gatedSiteReadOnly;

  return (
    <>
      <Helmet>
        <title>Drawings & Plans - SiteProof</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-20 pb-8 px-4 lg:px-6 max-w-7xl mx-auto">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <SiteReadOnlyBanner status={gatedSiteStatus} siteId={gatedSiteId} className="mb-6" />

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6 gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                {t('drawingsPlans')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t('drawingsPlansSubtitle')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                iconName="LayoutGrid"
                onClick={() => navigate('/drawings-gallery' + (selectedSite !== 'all' ? `?siteId=${selectedSite}` : ''))}
              >
                {t('galleryView')}
              </Button>
              {canUpload && (
                <Button
                  variant="default"
                  iconName="Upload"
                  onClick={() => setIsUploadModalOpen(true)}
                >
                  {t('uploadDrawing')}
                </Button>
              )}
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4 md:p-6 shadow-elevation-2 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-foreground">{t('filterDrawings')}</h3>
              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  className="text-sm text-accent hover:text-accent/80 font-medium flex items-center gap-1"
                >
                  <Icon name="X" size={14} />
                  {t('clearFilters')}
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="relative">
                <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={t('searchDrawingsPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="pl-10"
                />
              </div>
              <Select
                label={t('filterBySite')}
                options={siteOptions}
                value={selectedSite}
                onChange={setSelectedSite}
              />
              <Select
                label={t('filterByType')}
                options={drawingTypeOptions}
                value={selectedDrawingType}
                onChange={setSelectedDrawingType}
              />
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showLatestOnly}
                  onChange={(e) => setShowLatestOnly(e?.target?.checked)}
                  className="w-4 h-4 text-accent bg-background border-border rounded focus:ring-2 focus:ring-accent focus:ring-offset-2"
                />
                <span className="text-sm font-medium text-foreground">{t('showLatestOnly')}</span>
              </label>
            </div>
          </div>

          {loading ? (
            <Skeleton variant="card" count={6} />
          ) : error ? (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 text-center">
              <Icon name="AlertCircle" size={24} color="var(--color-error)" className="mx-auto mb-2" />
              <p className="text-error font-medium">{error}</p>
            </div>
          ) : filteredDrawings?.length === 0 ? (
            <div className="bg-card border border-border rounded-lg">
              <EmptyState
                icon={hasActiveFilters ? 'Search' : 'FileImage'}
                title={hasActiveFilters ? t('noMatchingDrawings') : t('noDrawingsFound')}
                description={
                  hasActiveFilters
                    ? t('tryAdjustingFilters')
                    : selectedSite !== 'all' ? t('noDrawingsForSite') : t('getStartedUploadDrawing')
                }
                ctaLabel={canUpload && !hasActiveFilters ? t('uploadDrawing') : hasActiveFilters ? t('clearFilters') : ''}
                ctaAction={canUpload && !hasActiveFilters ? () => setIsUploadModalOpen(true) : hasActiveFilters ? handleClearFilters : null}
                ctaIcon={!hasActiveFilters ? 'Upload' : 'X'}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredDrawings?.map(drawing => (
                <DrawingCard
                  key={drawing?.id}
                  drawing={drawing}
                  onViewVersions={() => handleViewVersions(drawing)}
                  onDownload={() => handleDownload(drawing)}
                  canUpload={canUpload}
                />
              ))}
            </div>
          )}
        </main>
      </div>

      {isUploadModalOpen && (
        <UploadDrawingModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onUpload={handleUploadDrawing}
          sites={sites}
        />
      )}

      {isVersionsModalOpen && selectedDrawing && (
        <DrawingVersionsModal
          isOpen={isVersionsModalOpen}
          onClose={() => {
            setIsVersionsModalOpen(false);
            setSelectedDrawing(null);
          }}
          drawing={selectedDrawing}
          onDownload={handleDownload}
        />
      )}
    </>
  );
};

export default DrawingsPlans;